import {
  LOOM_LEGACY_DEFAULT_MIGRATION_ID,
  createDefaultLoomWingBodyScene,
  isLegacyDefaultLoomWingBodyScene,
  parseLoomAuthoringDocument,
} from "./loomAuthoringModel";
import {
  acceptLoomAuthoringCommand,
  createLoomAuthoringHistory,
  currentLoomAuthoringDocument,
  parseLoomAuthoringHistory,
  type LoomAuthoringHistory,
} from "./loomAuthoringHistory";
import {
  createLoomControlWorkspaceHistory,
  parseLoomControlWorkspaceHistory,
  type LoomControlWorkspaceHistory,
} from "./loomControlWorkspaceHistory";

export const LOOM_HISTORY_STORAGE_V2_SCHEMA = "thread-loom-authoring-history-storage/v2" as const;
const LEGACY_HISTORY_SCHEMA = "loom-authoring-history/v1" as const;

export type LoomHistoryStorageUpgrade =
  | { kind: "fresh-v2" }
  | {
      kind: "legacy-default-migrated";
      sourceSchema: typeof LEGACY_HISTORY_SCHEMA;
      migrationId: typeof LOOM_LEGACY_DEFAULT_MIGRATION_ID;
      migrationOperationId: string;
    }
  | {
      kind: "legacy-v1-retained";
      sourceSchema: typeof LEGACY_HISTORY_SCHEMA;
      reason: "active-head-not-exact-legacy-default";
    };

export type LoomHistoryStorageStatus = LoomHistoryStorageUpgrade | {
  kind: "storage-blocked";
  reason: "cross-tab-conflict" | "coordination-unavailable" | "malformed-v1" | "malformed-v2" | "storage-unavailable";
  message: string;
};

export interface LoomAuthoringHistoryStorageV2 {
  schema: typeof LOOM_HISTORY_STORAGE_V2_SCHEMA;
  projectId: string;
  upgrade: LoomHistoryStorageUpgrade;
  history: LoomAuthoringHistory;
  workspaceHistory: LoomControlWorkspaceHistory;
}

export interface LoomAuthoringHistoryLoadResult {
  projectId: string;
  history: LoomAuthoringHistory;
  workspaceHistory: LoomControlWorkspaceHistory;
  status: LoomHistoryStorageStatus;
  persistence: "v2" | "blocked";
  persistedV2Raw: string | null;
}

export class LoomHistoryStorageConflictError extends Error {
  constructor() { super("Another tab changed this Loom journal; refusing to overwrite its branch."); }
}

export class LoomHistoryStorageCoordinationError extends Error {
  constructor() { super("This browser cannot serialize Loom history writes across tabs."); }
}

type UnknownRecord = Record<string, unknown>;

export function loomHistoryStorageV1Key(projectId: string): string {
  return `thread:loom:authoring-history:v1:${projectId}`;
}

export function loomHistoryStorageV2Key(projectId: string): string {
  return `thread:loom:authoring-history:v2:${projectId}`;
}

function record(value: unknown, path: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) throw new Error(`${path} must be an object`);
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unknown = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unknown.length > 0) throw new Error(`${path} contains unknown fields: ${unknown.join(", ")}`);
}

function nonEmpty(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${path} must be a non-empty string`);
  return value;
}

function parseUpgrade(value: unknown): LoomHistoryStorageUpgrade {
  const item = record(value, "storage.upgrade");
  if (item.kind === "fresh-v2") {
    exactKeys(item, ["kind"], "storage.upgrade");
    return { kind: "fresh-v2" };
  }
  if (item.kind === "legacy-default-migrated") {
    exactKeys(item, ["kind", "sourceSchema", "migrationId", "migrationOperationId"], "storage.upgrade");
    if (item.sourceSchema !== LEGACY_HISTORY_SCHEMA) throw new Error("storage.upgrade.sourceSchema is unsupported");
    if (item.migrationId !== LOOM_LEGACY_DEFAULT_MIGRATION_ID) throw new Error("storage.upgrade.migrationId is unsupported");
    return {
      kind: "legacy-default-migrated",
      sourceSchema: LEGACY_HISTORY_SCHEMA,
      migrationId: LOOM_LEGACY_DEFAULT_MIGRATION_ID,
      migrationOperationId: nonEmpty(item.migrationOperationId, "storage.upgrade.migrationOperationId"),
    };
  }
  if (item.kind === "legacy-v1-retained") {
    exactKeys(item, ["kind", "sourceSchema", "reason"], "storage.upgrade");
    if (item.sourceSchema !== LEGACY_HISTORY_SCHEMA || item.reason !== "active-head-not-exact-legacy-default") throw new Error("storage.upgrade retained reason is unsupported");
    return { kind: "legacy-v1-retained", sourceSchema: LEGACY_HISTORY_SCHEMA, reason: item.reason };
  }
  throw new Error("storage.upgrade.kind is unsupported");
}

function validateUpgrade(history: LoomAuthoringHistory, upgrade: LoomHistoryStorageUpgrade): void {
  const migrations = history.operations.filter((operation) => (
    operation.kind === "command.accepted"
    && operation.origin === "system.migration"
    && operation.command.kind === "system.migration"
  ));
  if (upgrade.kind === "legacy-default-migrated") {
    if (migrations.length !== 1 || migrations[0]?.operationId !== upgrade.migrationOperationId) throw new Error("storage migration receipt does not match the journal");
    const branchIndex = history.activeBranchOperationIds.indexOf(upgrade.migrationOperationId);
    if (branchIndex < 0 || history.cursor <= branchIndex) throw new Error("storage migration must remain on the active branch");
  } else if (migrations.length > 0) throw new Error("storage upgrade status omits a journal migration");
}

function validateWorkspaceGeometryBindings(
  history: LoomAuthoringHistory,
  workspaceHistory: LoomControlWorkspaceHistory,
): void {
  const geometryHeads = new Set([
    `${history.initialHead.stateId}\u0000${history.initialHead.revision}`,
    ...history.operations.flatMap((operation) => operation.kind === "command.accepted"
      ? [`${operation.result.stateId}\u0000${operation.result.revision}`]
      : []),
  ]);
  for (const operation of workspaceHistory.operations) {
    if (operation.kind !== "intent.accepted" || operation.documentStateId === undefined) continue;
    if (!geometryHeads.has(`${operation.documentStateId}\u0000${operation.documentRevision}`)) {
      throw new Error("workspace intent references a geometry state that is absent from this Pattern");
    }
  }
}

export function loomHistoryUserUndoFloor(history: LoomAuthoringHistory, status: LoomHistoryStorageStatus): number {
  if (status.kind !== "legacy-default-migrated") return 0;
  const branchIndex = history.activeBranchOperationIds.indexOf(status.migrationOperationId);
  return branchIndex < 0 ? history.cursor : branchIndex + 1;
}

export function parseLoomAuthoringHistoryStorageV2(value: unknown, expectedProjectId: string): LoomAuthoringHistoryStorageV2 {
  const item = record(value, "storage");
  exactKeys(item, ["schema", "projectId", "upgrade", "history", "workspaceHistory"], "storage");
  if (item.schema !== LOOM_HISTORY_STORAGE_V2_SCHEMA) throw new Error("storage.schema must be v2");
  const projectId = nonEmpty(item.projectId, "storage.projectId");
  if (projectId !== expectedProjectId) throw new Error("storage.projectId does not match its key");
  const upgrade = parseUpgrade(item.upgrade);
  const history = parseLoomAuthoringHistory(item.history);
  const workspaceHistory = parseLoomControlWorkspaceHistory(item.workspaceHistory);
  validateUpgrade(history, upgrade);
  validateWorkspaceGeometryBindings(history, workspaceHistory);
  return { schema: LOOM_HISTORY_STORAGE_V2_SCHEMA, projectId, upgrade, history, workspaceHistory };
}

function parseGeometryOnlyLoomHistoryStorageV2(
  value: unknown,
  expectedProjectId: string,
): LoomAuthoringHistoryStorageV2 {
  const item = record(value, "storage");
  exactKeys(item, ["schema", "projectId", "upgrade", "history"], "storage");
  if (item.schema !== LOOM_HISTORY_STORAGE_V2_SCHEMA) throw new Error("storage.schema must be v2");
  const projectId = nonEmpty(item.projectId, "storage.projectId");
  if (projectId !== expectedProjectId) throw new Error("storage.projectId does not match its key");
  const upgrade = parseUpgrade(item.upgrade);
  const history = parseLoomAuthoringHistory(item.history);
  validateUpgrade(history, upgrade);
  return {
    schema: LOOM_HISTORY_STORAGE_V2_SCHEMA,
    projectId,
    upgrade,
    history,
    workspaceHistory: createLoomControlWorkspaceHistory({
      kind: "geometry-only-v2-migrated",
      sourceSchema: LOOM_HISTORY_STORAGE_V2_SCHEMA,
    }),
  };
}

function documentAtInitialHead(history: LoomAuthoringHistory) {
  const state = history.states.find(({ stateId }) => stateId === history.initialHead.stateId);
  if (!state) throw new Error("legacy history initial state is missing");
  return parseLoomAuthoringDocument({ ...state.definition, revision: history.initialHead.revision });
}

function recognizedLegacyDefaultHistory(history: LoomAuthoringHistory): boolean {
  return history.initialHead.revision === 0
    && isLegacyDefaultLoomWingBodyScene(documentAtInitialHead(history))
    && isLegacyDefaultLoomWingBodyScene(currentLoomAuthoringDocument(history));
}

function migrationCommandId(history: LoomAuthoringHistory): string {
  const prefix = `system-migration-${LOOM_LEGACY_DEFAULT_MIGRATION_ID}-r${history.activeHead.revision}`;
  const used = new Set(history.operations.flatMap((operation) => operation.kind === "command.accepted" ? [operation.command.commandId] : []));
  let suffix = history.operations.length;
  while (used.has(`${prefix}-s${suffix}`)) suffix += 1;
  return `${prefix}-s${suffix}`;
}

function migrateLegacyDefault(history: LoomAuthoringHistory): LoomAuthoringHistoryStorageV2 {
  const migrated = acceptLoomAuthoringCommand(history, {
    schema: "loom-authoring-command/v1",
    commandId: migrationCommandId(history),
    expectedRevision: history.activeHead.revision,
    kind: "system.migration",
    migrationId: LOOM_LEGACY_DEFAULT_MIGRATION_ID,
  }, { origin: "system.migration", subjectIds: ["wing", "wing-mirrored", "wing-positioned", "vehicle"] });
  const operation = migrated.operations[migrated.operations.length - 1];
  if (!operation || operation.kind !== "command.accepted") throw new Error("system migration receipt is missing");
  return {
    schema: LOOM_HISTORY_STORAGE_V2_SCHEMA,
    projectId: "",
    upgrade: {
      kind: "legacy-default-migrated",
      sourceSchema: LEGACY_HISTORY_SCHEMA,
      migrationId: LOOM_LEGACY_DEFAULT_MIGRATION_ID,
      migrationOperationId: operation.operationId,
    },
    history: migrated,
    workspaceHistory: createLoomControlWorkspaceHistory(),
  };
}

function freshResult(projectId: string): LoomAuthoringHistoryStorageV2 {
  return {
    schema: LOOM_HISTORY_STORAGE_V2_SCHEMA,
    projectId,
    upgrade: { kind: "fresh-v2" },
    history: createLoomAuthoringHistory(createDefaultLoomWingBodyScene()),
    workspaceHistory: createLoomControlWorkspaceHistory(),
  };
}

function blocked(
  projectId: string,
  reason: Extract<LoomHistoryStorageStatus, { kind: "storage-blocked" }>["reason"],
  message: string,
): LoomAuthoringHistoryLoadResult {
  const fresh = freshResult("ephemeral");
  return { projectId, history: fresh.history, workspaceHistory: fresh.workspaceHistory, status: { kind: "storage-blocked", reason, message }, persistence: "blocked", persistedV2Raw: null };
}

export function loadLoomAuthoringHistoryStorage(storage: Storage | null, projectId: string): LoomAuthoringHistoryLoadResult {
  if (!storage) return blocked(projectId, "storage-unavailable", "Browser storage is unavailable; the journal is ephemeral.");
  let v2Raw: string | null;
  try { v2Raw = storage.getItem(loomHistoryStorageV2Key(projectId)); }
  catch (error) { return blocked(projectId, "storage-unavailable", error instanceof Error ? error.message : "Browser storage is unavailable."); }
  if (v2Raw !== null) {
    let decoded: unknown;
    try { decoded = JSON.parse(v2Raw); }
    catch (error) { return blocked(projectId, "malformed-v2", error instanceof Error ? error.message : "Stored v2 history is malformed."); }
    try {
      const envelope = parseLoomAuthoringHistoryStorageV2(decoded, projectId);
      return { projectId, history: envelope.history, workspaceHistory: envelope.workspaceHistory, status: envelope.upgrade, persistence: "v2", persistedV2Raw: v2Raw };
    } catch (error) {
      try {
        const envelope = parseGeometryOnlyLoomHistoryStorageV2(decoded, projectId);
        return { projectId, history: envelope.history, workspaceHistory: envelope.workspaceHistory, status: envelope.upgrade, persistence: "v2", persistedV2Raw: v2Raw };
      } catch {
        return blocked(projectId, "malformed-v2", error instanceof Error ? error.message : "Stored v2 history is malformed.");
      }
    }
  }
  let v1Raw: string | null;
  try { v1Raw = storage.getItem(loomHistoryStorageV1Key(projectId)); }
  catch (error) { return blocked(projectId, "storage-unavailable", error instanceof Error ? error.message : "Browser storage is unavailable."); }
  if (v1Raw === null) {
    const envelope = freshResult(projectId);
    return { projectId, history: envelope.history, workspaceHistory: envelope.workspaceHistory, status: envelope.upgrade, persistence: "v2", persistedV2Raw: null };
  }
  try {
    const legacy = parseLoomAuthoringHistory(JSON.parse(v1Raw));
    const envelope = recognizedLegacyDefaultHistory(legacy)
      ? { ...migrateLegacyDefault(legacy), projectId }
      : {
          schema: LOOM_HISTORY_STORAGE_V2_SCHEMA,
          projectId,
          upgrade: { kind: "legacy-v1-retained", sourceSchema: LEGACY_HISTORY_SCHEMA, reason: "active-head-not-exact-legacy-default" } as const,
          history: legacy,
          workspaceHistory: createLoomControlWorkspaceHistory(),
        };
    return { projectId, history: envelope.history, workspaceHistory: envelope.workspaceHistory, status: envelope.upgrade, persistence: "v2", persistedV2Raw: null };
  } catch (error) {
    return blocked(projectId, "malformed-v1", error instanceof Error ? error.message : "Stored v1 history is malformed.");
  }
}

export async function persistLoomAuthoringHistoryStorage(
  storage: Storage,
  projectId: string,
  history: LoomAuthoringHistory,
  workspaceHistory: LoomControlWorkspaceHistory,
  status: LoomHistoryStorageStatus,
  expectedV2Raw: string | null,
): Promise<string> {
  if (status.kind === "storage-blocked") throw new Error("Blocked history cannot be persisted");
  const envelope = parseLoomAuthoringHistoryStorageV2({
    schema: LOOM_HISTORY_STORAGE_V2_SCHEMA,
    projectId,
    upgrade: status,
    history,
    workspaceHistory,
  }, projectId);
  const lockManager = globalThis.navigator?.locks;
  if (!lockManager) throw new LoomHistoryStorageCoordinationError();
  const key = loomHistoryStorageV2Key(projectId);
  return lockManager.request(`thread:loom:history-write:${projectId}`, { mode: "exclusive" }, () => {
    const raw = JSON.stringify(envelope);
    const currentRaw = storage.getItem(key);
    if (currentRaw !== expectedV2Raw) {
      if (currentRaw === raw) return raw;
      throw new LoomHistoryStorageConflictError();
    }
    storage.setItem(key, raw);
    return raw;
  });
}
